import AddOutlined from "@mui/icons-material/AddOutlined";
import ArrowCircleRightOutlined from "@mui/icons-material/ArrowCircleRightOutlined";
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import CreditCardOffOutlined from "@mui/icons-material/CreditCardOffOutlined";
import NotificationsActiveOutlined from "@mui/icons-material/NotificationsActiveOutlined";
import ReplayOutlined from "@mui/icons-material/ReplayOutlined";
import RoomServiceOutlined from "@mui/icons-material/RoomServiceOutlined";
import SystemUpdateAltOutlined from "@mui/icons-material/SystemUpdateAltOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Fab from "@mui/material/Fab";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Zoom from "@mui/material/Zoom";
import { keyframes, styled } from "@mui/material/styles";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef } from "react";
import Draggable, { DraggableEventHandler } from "react-draggable";
import { useTranslation } from "react-i18next";
import Paper from "@mui/material/Paper";
import { toast } from "~served/ui-comps/toast";
import {
	isRingableOperationalRecord,
	RECORD_TYPE,
} from "~/components/operational-notification-ringing";
import { useOrderRinging } from "~/hooks/use-order-ringing";
import { tokens } from "~/locales/tokens";
import { queryClient } from "~/providers/query-provider";
import { useAuthCache } from "~/queries/use-auth-cache";
import { useGetCurrentVenue } from "~/queries/use-get-current-venue";
import { useGetOperationalNotifications } from "~/queries/use-get-operational-notifications";
import { usePlacedOrders } from "~/queries/use-get-operational-orders";
import { useResolveAssistance } from "~/queries/use-resolve-assistance";
import { setState, useIsReady, useStore } from "~/store";
import { getPaywayOrderFailureMessage } from "~/utils/payway-order-failure-message";
import {
	shouldNotifyForDigitalMenuLocation,
	shouldNotifyForDigitalMenuOrder,
	useDigitalMenuNotificationLocationIds,
} from "~/utils/digital-menu-notification-locations";
import { usePopover } from "~served/ui-comps/hooks/use-popover";
import { dayjs } from "~served/utils/libs/dayjs";

/** Pointer movement below this still counts as a click, not a drag. */
const DRAG_CLICK_TOLERANCE_PX = 4;

export const OperationalNotification = () => {
	const router = useRouter();
	const { t } = useTranslation();

	const isUpdateAvailable = useStore((s) => s.isUpdateAvailable);
	const isUpdateInstalled = useStore((s) => s.isUpdateInstalled);
	const { data: venue } = useGetCurrentVenue();
	const isReady = useIsReady();
	const { data: auth } = useAuthCache();
	const { data: orders } = usePlacedOrders();
	const [digitalMenuNotificationLocationIds] =
		useDigitalMenuNotificationLocationIds();
	const visibleOrders = useMemo(
		() =>
			orders.filter((order) =>
				shouldNotifyForDigitalMenuOrder(
					order,
					digitalMenuNotificationLocationIds,
				),
			),
		[orders, digitalMenuNotificationLocationIds],
	);
	const prevOrders = useRef(visibleOrders);
	const { data: notifications = [] } = useGetOperationalNotifications();
	const visibleNotifications = useMemo(
		() =>
			notifications.filter(
				(n) =>
					n.kind === "payway_order_failure" ||
					shouldNotifyForDigitalMenuLocation(
						n._location?._id ?? n.location,
						digitalMenuNotificationLocationIds,
					),
			),
		[notifications, digitalMenuNotificationLocationIds],
	);
	const popover = usePopover<HTMLButtonElement>();
	const resolveAssistance = useResolveAssistance();
	const resolveAssistancePopover = usePopover<
		HTMLButtonElement,
		{ id: string }
	>({ id: "" });
	const dragNodeRef = useRef<HTMLDivElement>(null);
	const dragStartRef = useRef({ x: 0, y: 0 });
	// Releasing a drag also fires a click on the button; that click must not open the list.
	const wasDraggedRef = useRef(false);

	const handleDragStart: DraggableEventHandler = (_, { x, y }) => {
		dragStartRef.current = { x, y };
		wasDraggedRef.current = false;
	};

	const openList = () => {
		if (!auth) {
			toast.error("Login first", { persist: false });
			return;
		}
		popover.handleOpen();
	};

	const handleDragStop: DraggableEventHandler = (event, { x, y }) => {
		const { x: startX, y: startY } = dragStartRef.current;
		wasDraggedRef.current =
			Math.hypot(x - startX, y - startY) > DRAG_CLICK_TOLERANCE_PX;
		// Draggable cancels touchstart, so a tap never produces a click.
		if (event.type === "touchend" && !wasDraggedRef.current) openList();
	};
	const records = useMemo<
		(
			| { $type: RECORD_TYPE.UPDATE_AVAILABLE }
			| { $type: RECORD_TYPE.UPDATE_INSTALLED }
			| ({ $type: RECORD_TYPE.ORDER } & (typeof orders)[0])
			| ({ $type: RECORD_TYPE.NOTIFICATION } & (typeof notifications)[0])
		)[]
		// @ts-ignore
	>(() => {
		return [
			...(isUpdateAvailable ? [{ $type: RECORD_TYPE.UPDATE_AVAILABLE }] : []),
			...(!isUpdateInstalled ? [{ $type: RECORD_TYPE.UPDATE_INSTALLED }] : []),
			...[
				...visibleOrders.map((o) => ({ ...o, $type: RECORD_TYPE.ORDER })),
				...visibleNotifications.map((o) => ({
					...o,
					$type: RECORD_TYPE.NOTIFICATION,
				})),
			].sort((a, b) => dayjs(b.created_at).diff(dayjs(a.created_at))),
		];
	}, [
		visibleOrders,
		visibleNotifications,
		isUpdateAvailable,
		isUpdateInstalled,
	]);

	// track orders no longer in the `placed` list and close their snackbar
	useEffect(() => {
		prevOrders.current
			.filter((o) => visibleOrders.every((no) => no._id !== o._id))
			.map((o) => o._id)
			.map(toast.dismiss);
		prevOrders.current = visibleOrders;

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visibleOrders]);

	useOrderRinging({
		enabled:
			isReady &&
			records.some((record) =>
				isRingableOperationalRecord({
					type: record.$type,
					pathname: router.pathname,
					isKdsEnabled: venue?.is_kds_enabled ?? false,
				}),
			),
		dings: venue?.order_notification_dings ?? 0,
	});

	const dismissPopover = usePopover<
		HTMLButtonElement,
		RECORD_TYPE.UPDATE_AVAILABLE | RECORD_TYPE.UPDATE_INSTALLED | null
	>(null);

	if (!isReady) return null;

	return (
		// Mounted at the badge's resting position, zooming the dragged node's content so it
		// scales in and out in place; the wrapper stays click-through while it is hidden.
		<Box
			sx={{
				position: "fixed",
				zIndex: 1299,
				left: 430,
				top: 12,
				pointerEvents: "none",
			}}>
			<Draggable
				nodeRef={dragNodeRef}
				onStart={handleDragStart}
				onStop={handleDragStop}>
				<Box ref={dragNodeRef}>
					<Zoom in={!!records.length}>
						<Badge
							hidden={!records.length}
							badgeContent={records.length}
							color="primary"
							sx={{ pointerEvents: "auto" }}>
							<Fab
								ref={popover.anchorRef}
								onClick={() => {
									if (wasDraggedRef.current) {
										wasDraggedRef.current = false;
										return;
									}
									openList();
								}}>
								<NotificationsActiveOutlined />
							</Fab>

							<BlinkedCircularProgress
								color="error"
								thickness={2}
								size={48}
								variant="determinate"
								value={100}
							/>

							<Popover
								anchorEl={popover.anchorRef.current}
								anchorOrigin={{
									horizontal: "center",
									vertical: "bottom",
								}}
								transformOrigin={{
									horizontal: "center",
									vertical: "top",
								}}
								disableScrollLock
								open={popover.isOpen}
								onClose={popover.handleClose}
								PaperProps={{ sx: { width: 380 } }}>
								<Stack
									alignItems="center"
									direction="row"
									justifyContent="space-between"
									spacing={2}
									sx={{ px: 3, py: 2 }}>
									<Typography
										color="inherit"
										variant="h6">
										Notifications
									</Typography>
								</Stack>
								<List disablePadding>
									{records.map((obj) => {
										if (obj.$type === RECORD_TYPE.ORDER)
											return (
												<ListItem
													component={Paper}
													key={obj._id}
													secondaryAction={
														<IconButton
															color="primary"
															onClick={async () => {
																if (!auth) {
																	toast.error("Login first", {
																		persist: false,
																	});
																	return;
																}
																await router.push(`/ops/orders?id=${obj._id}`);
																popover.handleClose();
															}}>
															<ArrowCircleRightOutlined />
														</IconButton>
													}>
													<ListItemAvatar>
														<Avatar>
															<AddOutlined />
														</Avatar>
													</ListItemAvatar>
													<ListItemText
														primary={
															<Box
																sx={{
																	alignItems: "center",
																	display: "flex",
																	flexWrap: "wrap",
																}}>
																<Typography
																	sx={{ mr: 0.5 }}
																	variant="subtitle2">
																	{obj._location.name}
																</Typography>
																<Typography
																	sx={{ mr: 0.5 }}
																	variant="body2">
																	has a new order
																</Typography>
																<Typography variant="body2">
																	#{obj.index}
																</Typography>
															</Box>
														}
														secondary={
															<Typography
																color="text.secondary"
																variant="caption">
																{dayjs(obj.created_at).fromNow()}
															</Typography>
														}
														sx={{ my: 0 }}
													/>
												</ListItem>
											);

										if (obj.$type === RECORD_TYPE.NOTIFICATION)
											return (
												<ListItem
													component={Paper}
													key={obj._id}
													selected={
														resolveAssistancePopover.data?.id === obj._id
													}
													secondaryAction={
														<IconButton
															color="primary"
															onClick={(e) => {
																resolveAssistancePopover.anchorRef.current =
																	e.currentTarget;

																if (!auth) {
																	toast.error("Login first", {
																		persist: false,
																	});
																	return;
																}
																resolveAssistancePopover.handleOpen({
																	id: obj._id,
																});
															}}>
															<CheckCircleOutlined />
														</IconButton>
													}>
													<ListItemAvatar>
														{obj.kind === "payway_order_failure" ? (
															<Avatar sx={{ bgcolor: "error.main" }}>
																<CreditCardOffOutlined />
															</Avatar>
														) : (
															<Avatar>
																<RoomServiceOutlined />
															</Avatar>
														)}
													</ListItemAvatar>
													<ListItemText
														primary={
															<Box
																sx={{
																	alignItems: "center",
																	display: "flex",
																	flexWrap: "wrap",
																}}>
																<Typography
																	sx={{ mr: 0.5 }}
																	variant="subtitle2">
																	{obj._location.name}
																</Typography>
																{!(
																	obj.kind === "payway_order_failure" ||
																	obj.content
																) && (
																	<Typography
																		sx={{ mr: 0.5 }}
																		variant="body2">
																		needs staff
																	</Typography>
																)}
															</Box>
														}
														secondary={
															<>
																{(obj.kind === "payway_order_failure" ||
																	obj.content) && (
																	<Typography
																		color={
																			obj.kind === "payway_order_failure"
																				? "error"
																				: "text.primary"
																		}
																		variant="body2">
																		{obj.kind === "payway_order_failure"
																			? getPaywayOrderFailureMessage(obj)
																			: obj.content}
																	</Typography>
																)}
																<Typography
																	color="text.secondary"
																	variant="caption">
																	{dayjs(obj.created_at).fromNow()}
																</Typography>
															</>
														}
														sx={{ my: 0 }}
													/>
												</ListItem>
											);

										if (obj.$type === RECORD_TYPE.UPDATE_AVAILABLE)
											return (
												<ListItem
													component={Paper}
													key={RECORD_TYPE.UPDATE_AVAILABLE}
													secondaryAction={
														<Stack direction="row">
															<IconButton
																color="primary"
																onClick={() => {
																	queryClient.clear();
																	window.location.reload();
																}}>
																<ReplayOutlined />
															</IconButton>
															<IconButton
																onClick={(e) => {
																	dismissPopover.anchorRef.current =
																		e.currentTarget;
																	dismissPopover.handleOpen(
																		RECORD_TYPE.UPDATE_AVAILABLE,
																	);
																}}>
																<CloseOutlined />
															</IconButton>
														</Stack>
													}>
													<ListItemAvatar>
														<Avatar>
															<SystemUpdateAltOutlined />
														</Avatar>
													</ListItemAvatar>
													<ListItemText
														primary={
															<Typography
																sx={{ mr: 0.5 }}
																variant="subtitle2">
																Update available
															</Typography>
														}
														secondary={
															<Typography
																color="text.primary"
																variant="body2">
																Reload now to install new version
															</Typography>
														}
														sx={{ my: 0 }}
													/>
												</ListItem>
											);

										if (obj.$type === RECORD_TYPE.UPDATE_INSTALLED)
											return (
												<ListItem
													component={Paper}
													key={RECORD_TYPE.UPDATE_INSTALLED}
													secondaryAction={
														<Stack direction="row">
															<IconButton
																color="primary"
																onClick={() => {
																	queryClient.clear();
																	window.location.reload();
																}}>
																<ReplayOutlined />
															</IconButton>
															<IconButton
																onClick={(e) => {
																	dismissPopover.anchorRef.current =
																		e.currentTarget;
																	dismissPopover.handleOpen(
																		RECORD_TYPE.UPDATE_INSTALLED,
																	);
																}}>
																<CloseOutlined />
															</IconButton>
														</Stack>
													}>
													<ListItemAvatar>
														<Avatar>
															<SystemUpdateAltOutlined />
														</Avatar>
													</ListItemAvatar>
													<ListItemText
														primary={
															<Typography
																sx={{ mr: 0.5 }}
																variant="subtitle2">
																Update installed
															</Typography>
														}
														secondary={
															<Typography
																color="text.primary"
																variant="body2">
																Reload now to finish the update
															</Typography>
														}
														sx={{ my: 0 }}
													/>
												</ListItem>
											);

										return null;
									})}
								</List>

								<Popover
									anchorEl={resolveAssistancePopover.anchorRef.current}
									anchorOrigin={{
										horizontal: "right",
										vertical: "bottom",
									}}
									transformOrigin={{
										horizontal: "right",
										vertical: "top",
									}}
									disableScrollLock
									open={resolveAssistancePopover.isOpen}
									onClose={resolveAssistancePopover.handleClose}>
									<Stack
										spacing={2}
										sx={{ px: 3, py: 2 }}>
										<Typography variant="body2">
											Mark this assistance as resolved?
										</Typography>
										<Box
											display="flex"
											width={1}
											gap={2}
											justifyContent="flex-end">
											<LoadingButton
												variant="outlined"
												color="inherit"
												onClick={resolveAssistancePopover.handleClose}>
												{t(tokens.global.cancel)}
											</LoadingButton>
											<LoadingButton
												color="primary"
												loading={resolveAssistance.isLoading}
												onClick={() =>
													resolveAssistance.mutate(
														{ id: resolveAssistancePopover.data!.id },
														{ onSuccess: resolveAssistancePopover.handleClose },
													)
												}>
												{t(tokens.global.yes)}
											</LoadingButton>
										</Box>
									</Stack>
								</Popover>

								<Popover
									anchorEl={dismissPopover.anchorRef.current}
									anchorOrigin={{
										horizontal: "right",
										vertical: "bottom",
									}}
									transformOrigin={{
										horizontal: "right",
										vertical: "top",
									}}
									disableScrollLock
									open={dismissPopover.isOpen}
									onClose={dismissPopover.handleClose}>
									<Stack
										spacing={2}
										sx={{ px: 3, py: 2 }}>
										<Typography variant="body2">
											Reload the page before dismissing this
										</Typography>
										<div
											style={{ display: "flex", justifyContent: "flex-end" }}>
											<Button
												color="primary"
												onClick={() => {
													if (
														dismissPopover.data === RECORD_TYPE.UPDATE_AVAILABLE
													)
														setState({ isUpdateAvailable: false });

													if (
														dismissPopover.data === RECORD_TYPE.UPDATE_INSTALLED
													)
														setState({ isUpdateInstalled: true });

													dismissPopover.handleClose();
												}}>
												dismiss
											</Button>
										</div>
									</Stack>
								</Popover>
							</Popover>
						</Badge>
					</Zoom>
				</Box>
			</Draggable>
		</Box>
	);
};

const ping = keyframes`
		75%, 100% {
			transform: scale(1.15);
			opacity: 0;
		}
	`;

const BlinkedCircularProgress = styled(CircularProgress)({
	position: "absolute",
	right: 0,
	zIndex: 99,
	animation: `${ping} 1s cubic-bezier(0, 0, 0.2, 1) infinite`,
});
